import {
  fulfillmentServiceProviderName,
  normalizeFulfillmentResourceType,
} from "../../fulfillment-order/service";
import type { FulfillmentOrder, FulfillmentOrderLine } from "../../fulfillment-order/types";

export type OrderHandoverGuideGroup = {
  key: string;
  guideName: string;
  settlementSupplierId: string | null;
  lines: FulfillmentOrderLine[];
  existingOrderId: string | null;
};

export type GuideContractSigningPlan = {
  settlementSupplierId: string;
  guideName: string;
  selectedLineNos: number[];
  lineIds: string[];
  replaceDraftAgreement: boolean;
};

export type GuideContractSigningPlanResult =
  | { ok: true; plan: GuideContractSigningPlan }
  | { ok: false; error: string };

/** 从确认件行中筛出有效导游行，并按服务供应商（导游）分组。 */
export function groupActiveGuideLines(
  lines: readonly FulfillmentOrderLine[],
  orders: readonly FulfillmentOrder[] = [],
): OrderHandoverGuideGroup[] {
  const orderById = new Map(orders.map((order) => [order.id, order]));
  const groups = new Map<string, OrderHandoverGuideGroup>();
  for (const line of lines) {
    if (line.status !== "active") continue;
    if (normalizeFulfillmentResourceType(line.resourceType) !== "导游") continue;
    const guideName =
      fulfillmentServiceProviderName(line)?.trim()
      || line.fulfillmentSupplierName?.trim()
      || "未分配导游";
    const settlementSupplierId = line.fulfillmentSupplierId?.trim() || null;
    const key = settlementSupplierId || `name:${guideName}`;
    const existing = groups.get(key);
    if (existing) {
      existing.lines.push(line);
      continue;
    }
    groups.set(key, {
      key,
      guideName,
      settlementSupplierId,
      lines: [line],
      existingOrderId: null,
    });
  }
  return Array.from(groups.values())
    .map((group) => {
      const assignedOrderIds = [
        ...new Set(
          group.lines
            .map((line) => line.fulfillmentOrderId?.trim() || "")
            .filter(Boolean),
        ),
      ];
      const existingOrderId = assignedOrderIds.find((orderId) => {
        const order = orderById.get(orderId);
        return order && (
          !group.settlementSupplierId
          || order.settlementSupplierId === group.settlementSupplierId
        );
      }) ?? assignedOrderIds[0] ?? null;
      return {
        ...group,
        existingOrderId,
        lines: [...group.lines].sort((left, right) => left.lineNo - right.lineNo),
      };
    })
    .sort((left, right) => left.guideName.localeCompare(right.guideName, "zh"));
}

/**
 * 按勾选费用行组装生成合同计划；未勾选不得生成。
 * 可含已归口草稿行，生成时作废原草稿并重建。
 */
export function buildGuideContractSigningPlan(
  lines: readonly FulfillmentOrderLine[],
  selectedIds: readonly string[],
): GuideContractSigningPlanResult {
  const selectedIdSet = new Set(selectedIds);
  const selected = lines.filter((line) => selectedIdSet.has(line.id));
  if (selected.length === 0) {
    return { ok: false, error: "请先勾选导游费用行后再生成合同" };
  }
  const invalidType = selected.find(
    (line) => normalizeFulfillmentResourceType(line.resourceType) !== "导游",
  );
  if (invalidType) {
    return { ok: false, error: "只能勾选导游费用行后生成合同" };
  }
  const inactive = selected.find((line) => line.status !== "active");
  if (inactive) {
    return { ok: false, error: "所选包含无效费用行，请重新勾选" };
  }
  const supplierIds = new Set(
    selected
      .map((line) => line.fulfillmentSupplierId?.trim() || "")
      .filter(Boolean),
  );
  if (supplierIds.size === 0) {
    return { ok: false, error: "所选导游尚未绑定履约主体，请先在确认件中指定履约导游" };
  }
  if (supplierIds.size > 1) {
    return { ok: false, error: "请勾选同一导游的费用行后再生成合同" };
  }
  const settlementSupplierId = [...supplierIds][0]!;
  const guideName =
    fulfillmentServiceProviderName(selected[0]!)?.trim()
    || selected[0]?.fulfillmentSupplierName?.trim()
    || "导游";
  const replaceDraftAgreement = selected.some((line) => Boolean(line.fulfillmentOrderId));
  return {
    ok: true,
    plan: {
      settlementSupplierId,
      guideName,
      selectedLineNos: selected.map((line) => line.lineNo).sort((left, right) => left - right),
      lineIds: selected.map((line) => line.id),
      replaceDraftAgreement,
    },
  };
}
